<template>
  <div>
    <Toolbar
      v-if="show_toolbar"
      :moduleName="moduleName"
      :sortableHeaders="sortableHeaders"
      :config="config"
      :onCreate="onCreate"
      :exportCsv="exportCsv"
      :title="title"
      :availableViews="availableViews"
      :key="moduleName"
      :client="client"
      :clientId="clientId"
      :product_id="product_id"
      :medium="medium"
      :tabs="tabs"
      :tabFilterKey="tabFilterKey"
      :showTabs="showTabs"
      :showTotalsToggle="showTotalsToggle"
      :totalsVisible="totalsVisible"
      @toggle-totals="emit('toggle-totals')"
    >
      <template #before-create>
        <slot name="before-create" />
      </template>
    </Toolbar>
    <component
      :is="currentViewComponent"
      :moduleName="moduleName"
      :headers="headers"
      :data="data"
      :config="config"
      :onUpdate="onUpdate"
      :onRecordUpdate="onRecordUpdate"
      :onDelete="onDelete"
      @viewItem="openDetailDialog"
      @editItem="openEditDialog"
      :title="title"
      :statusField="statusField"
      :rowActionHandlers="rowActionHandlers"
      :client="client"
      :clientId="clientId"
      :key="moduleName"
      :product_id="product_id"
      :selectable="selectable"
      :selectedItems="selectedItems"
      @update:selectedItems="emit('update:selectedItems', $event)"
    />

    <Pagination v-if="show_pagination" :moduleName="moduleName" :title="title"/>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted, watch } from 'vue';
import { usePreferencesStore } from '@/store/preferences';
import { useLayoutField } from '@/composables/useLayoutField';
import { useHydratedHeaders } from '@/composables/useHydratedHeaders';
import Toolbar from '@/components/layout/Toolbar.vue';
import Pagination from '@/components/layout/Pagination.vue';
import { configRegistry } from '@/lib/configRegistry';
import { useDialogsStore } from '@/store/dialogs';
import { useRoute } from 'vue-router';

// === PROPS ===
const props = defineProps<{
  moduleName: string;
  listViewComponent: any;
  calendarViewComponent: any;
  kanbanViewComponent: any;
  gridViewComponent: any;
  cardViewComponent: any;
  filterComponent?: any;
  data: any;
  config: string;
  onCreate: (data: any) => Promise<any>;
  exportCsv: (queryString: string, body: any) => Promise<any>;
  onUpdate: (id: number, data: any) => Promise<any>;
  onRecordUpdate?: (id: number, data: any) => Promise<any>;
  onDelete?: (id: number) => Promise<any>;
  show_toolbar: boolean;
  show_pagination: boolean;
  title: any;
  rowActionHandlers: {
    type: Object,
    required: false,
    default: () => ({})
  };
  client?: any;
  clientId?: number;
  product_id?:number;
  medium?:any;
  selectable?: boolean;
  selectedItems?: Array<number | string>;
  tabs?: Array<{ label: string; value: any }>;
  tabFilterKey?: string;
  showTabs?: boolean;
  showTotalsToggle?: boolean;
  totalsVisible?: boolean;
}>();

const emit = defineEmits<{
  (e: 'toggle-totals'): void;
  (e: 'update:selectedItems', payload: Array<number | string>): void;
}>();

const preferencesStore = usePreferencesStore();
const statusField = ref<string>('');

// === VISTAS DISPONIBLES ===
const currentViewComponent = computed(() => {
  switch (useLayoutField(props.moduleName, 'currentView').value) {
    case 'table':
      return props.listViewComponent;
    case 'grid':
      return props.gridViewComponent;
    case 'card':
      return props.cardViewComponent;
    case 'calendar':
      return props.calendarViewComponent;
    case 'kanban':
      return props.kanbanViewComponent;
    default:
      return props.listViewComponent;
  }
});

const availableViews = computed(() => {
  const views = [];
  if (props.listViewComponent) views.push('table');
  if (props.gridViewComponent) views.push('grid');
  if (props.cardViewComponent) views.push('card');
  if (props.calendarViewComponent) views.push('calendar');
  if (props.kanbanViewComponent) views.push('kanban');
  return views;
});


// === HEADERS POR MÓDULO (no más contaminación)
const headers = useHydratedHeaders(props.moduleName);

// === HEADERS ORDENABLES POR MÓDULO
const sortableHeaders = computed(() =>
  preferencesStore.getAllHeadersByModule(props.moduleName).filter((h) => h.sortable)
);

// === ABRIR DETALLE VÍA DIALOGS STORE GLOBAL ===
const dialogsStore = useDialogsStore();

const openDetailDialog = async (item: any, module_name?: string) => {
  const id = item?.id ?? item;
  let moduleName = module_name || props.moduleName as string;

  // Busca el componente DETAIL dinámico de ese módulo
  const loader = configRegistry[moduleName];
  let component: any;
  if (loader) {
    const mod = await loader();
    component = mod?.DETAIL?.component;
  } else {
    console.warn(`[${moduleName}] No se encontró DETAIL.component en configRegistry`);
    return;
  }

  dialogsStore.open(
    moduleName,
    id,
    {
      id, // para el prop id
      client: props.client,
      clientId: props.clientId,
      onUpdate: props.onUpdate,
      rowActionHandlers: props.rowActionHandlers,
      // agrega aquí cualquier prop extra que tu detalle necesite
    }
  );
};

const route = useRoute();
function openDialogIfIdPresent() {
  const id = route.params.id;
  const module = route.meta.moduleName || route.name; // O ajusta esto a tus necesidades
  if (id && module) {
    dialogsStore.open(
      module as string,
      Number(id),
      {
        id: Number(id),
        client: props.client,
        clientId: props.clientId,
        onUpdate: props.onUpdate,
        rowActionHandlers: props.rowActionHandlers,
      }
    );
  }
}

onMounted(async () => {
  const loader = configRegistry[props.moduleName];
  if (loader) {
    const mod = await loader();
    statusField.value = mod?.KANBAN?.statusField ?? 'status'; // fallback genérico
  }
});

onMounted(openDialogIfIdPresent);
watch(() => route.fullPath, openDialogIfIdPresent);


</script>

<script lang="ts">
export default {};
</script>
